import { useCallback, useState } from 'react';
import {
  findCampaignOverlay,
  removeDocValueOverlays,
  variableNameKey,
} from '../../../../utils/variableScope';
import { renameVarsInVariableRows } from '../../../DocEditor/utils/variableUtils';
import { showToast } from '../../../Toast';

export default function useVariableRename({
  docRowsRef,
  campaignRowsRef,
  onDocRef,
  onCampRef,
  onRenameVariableRef,
  onPatch,
}) {
  const [pendingRename, setPendingRename] = useState(null);

  /** Apply rename + nested `{{refs}}` in the editable rows of this scope. */
  const applyLocalRename = useCallback((scope, id, oldName, newName) => {
    const trimmed = String(newName || '')
      .trim()
      .replace(/\s+/g, '_');
    const from = String(oldName || '');
    if (!trimmed || from === trimmed) return;

    const rows =
      scope === 'doc' ? docRowsRef.current : campaignRowsRef.current;
    const onChange = scope === 'doc' ? onDocRef.current : onCampRef.current;
    if (!onChange) return;

    if (
      rows.some(
        (v) => v.id !== id && variableNameKey(v.name) === variableNameKey(trimmed)
      )
    ) {
      const reverted = rows.map((v) => (v.id === id ? { ...v, name: from } : v));
      if (scope === 'doc') docRowsRef.current = reverted;
      else campaignRowsRef.current = reverted;
      onChange(reverted, { silent: false });
      showToast(`“${trimmed}” already exists`, { type: 'error' });
      return;
    }

    let base = rows;
    if (scope === 'doc') {
      const row = rows.find((v) => v.id === id);
      const overlay = row ? findCampaignOverlay(campaignRowsRef.current, row) : null;
      const overlayHoldsValue =
        overlay && (overlay.docOverlay || overlay.id === row.id);
      if (row && row.valueScope !== 'mission' && overlayHoldsValue) {
        base = rows.map((v) =>
          v.id === id
            ? {
                ...v,
                value: overlay.value ?? v.value,
                ...(Array.isArray(overlay.values) ? { values: [...overlay.values] } : {}),
                ...(overlay.type ? { type: overlay.type } : {}),
                valueScope: 'doc',
              }
            : v
        );
        const stripped = removeDocValueOverlays(campaignRowsRef.current, row);
        campaignRowsRef.current = stripped;
        onCampRef.current?.(stripped);
      } else {
        const target = (campaignRowsRef.current || []).find(
          (v) =>
            v &&
            (v.id === id ||
              (v.docOverlay && variableNameKey(v.name) === variableNameKey(from)))
        );
        const nextCamp = renameVarsInVariableRows(campaignRowsRef.current || [], from, trimmed, {
          renamedId: target?.id || null,
        }).rows;
        campaignRowsRef.current = nextCamp;
        onCampRef.current?.(nextCamp);
      }
    }

    const { rows: next, count } = renameVarsInVariableRows(base, from, trimmed, {
      renamedId: id,
    });
    if (scope === 'doc') docRowsRef.current = next;
    else campaignRowsRef.current = next;
    onChange(next, { silent: false });

    const refNote =
      count > 0
        ? ` · updated ${count} nested reference${count === 1 ? '' : 's'}`
        : '';
    const otherDocsNote =
      scope === 'campaign' ? ' · other docs not updated' : '';
    showToast(`Renamed ${from} → ${trimmed}${refNote}${otherDocsNote}`, {
      type: scope === 'campaign' ? 'info' : 'success',
      duration: 6000,
    });
  }, [docRowsRef, campaignRowsRef, onDocRef, onCampRef]);

  const onRename = useCallback(
    (scope, id, oldName, newName) => {
      const trimmed = String(newName || '')
        .trim()
        .replace(/\s+/g, '_');
      const from = String(oldName || '').trim();
      if (!trimmed || !from || from === trimmed) {
        onPatch(scope, id, { name: trimmed || newName || '' }, { silent: false });
        return;
      }

      // DocEditor path — rewrite pills/blocks + undo toast.
      // Never fall through to applyLocalRename after a successful/conflict
      // handle — that path only patches the row and leaves pills detached.
      if (scope === 'doc' && onRenameVariableRef.current) {
        const handled = onRenameVariableRef.current(scope, {
          id,
          oldName: from,
          newName: trimmed,
        });
        if (handled === 'conflict') {
          docRowsRef.current = docRowsRef.current.map((v) =>
            v.id === id ? { ...v, name: from } : v
          );
          return;
        }
        if (handled === true) {
          const { rows } = renameVarsInVariableRows(
            docRowsRef.current,
            from,
            trimmed,
            { renamedId: id }
          );
          docRowsRef.current = rows;
          return;
        }
        // Handler declined — plain name patch
        onPatch(scope, id, { name: trimmed }, { silent: false });
        return;
      }

      // Campaign renames may break other docs — confirm first
      if (scope === 'campaign') {
        setPendingRename({ scope, id, oldName: from, newName: trimmed });
        return;
      }

      applyLocalRename(scope, id, from, trimmed);
    },
    [onPatch, applyLocalRename, onRenameVariableRef, docRowsRef]
  );

  const confirmPendingRename = () => {
    if (!pendingRename) return;
    const { scope, id, oldName, newName } = pendingRename;
    setPendingRename(null);
    applyLocalRename(scope, id, oldName, newName);
  };

  const cancelPendingRename = () => {
    if (!pendingRename) return;
    const { scope, id, oldName } = pendingRename;
    setPendingRename(null);
    // Silent typing already wrote the new name — restore the old one
    onPatch(scope, id, { name: oldName }, { silent: false });
  };

  return {
    pendingRename,
    onRename,
    confirmPendingRename,
    cancelPendingRename,
  };
}
